import type { Child } from "@spork/s4p-server-sdk";
import { useCallback, useEffect, useRef } from "react";
import {
    Platform,
    Pressable,
    ScrollView,
    View,
    type ScrollView as ScrollViewType,
} from "react-native";

import { Text } from "@/components/ui/text";
import { getChildLabel } from "@/features/selectedChild/getChildLabel";
import { cn } from "@/lib/utils";
import { TEST_IDS } from "@/testIdDefinition";

type ChildSelectorProps = {
    childList: Child[];
    selectedChildIds: number[];
    isLoading: boolean;
    onSelectChild: (childId: number) => void;
    disabled?: boolean;
};

type ChildChipProps = {
    child: Child;
    isSelected: boolean;
    onSelectChild?: (childId: number) => void;
    onLayout?: (childId: number, x: number, width: number) => void;
    disabled?: boolean;
};

function ChildChip({
    child,
    isSelected,
    onSelectChild,
    onLayout,
    disabled,
}: ChildChipProps) {
    const label = getChildLabel(child);

    if (disabled) {
        return (
            <View
                className={cn(
                    "rounded-pill border px-3 py-1.5",
                    isSelected
                        ? "border-primary bg-primary"
                        : "border-border bg-card opacity-50",
                )}
            >
                <Text
                    className={
                        isSelected
                            ? "text-primary-foreground"
                            : "text-foreground"
                    }
                >
                    {label}
                </Text>
            </View>
        );
    }

    return (
        <View
            onLayout={(event) => {
                const { x, width } = event.nativeEvent.layout;
                onLayout?.(child.id, x, width);
            }}
        >
            <Pressable
                accessibilityRole="button"
                accessibilityState={{ selected: isSelected }}
                accessibilityLabel={label}
                onPress={() => onSelectChild?.(child.id)}
                testID={TEST_IDS[TEST_IDS.STUDENT_CHIP]}
                className={cn(
                    "rounded-pill border px-3 py-1.5 active:opacity-80",
                    isSelected
                        ? "border-primary bg-primary"
                        : "border-border bg-card",
                )}
            >
                <Text
                    variant="small"
                    className={
                        isSelected
                            ? "text-primary-foreground"
                            : "text-foreground"
                    }
                >
                    {label}
                </Text>
            </Pressable>
        </View>
    );
}

type NativeChildChipCarouselProps = {
    childList: Child[];
    selectedChildIds: number[];
    onSelectChild: (childId: number) => void;
};

function NativeChildChipCarousel({
    childList,
    selectedChildIds,
    onSelectChild,
}: NativeChildChipCarouselProps) {
    const scrollRef = useRef<ScrollViewType>(null);
    const scrollViewWidthRef = useRef(0);
    const contentWidthRef = useRef(0);
    const chipLayoutsRef = useRef(
        new Map<number, { x: number; width: number }>(),
    );
    const hasInitialScrollRef = useRef(false);
    const focusChildId = selectedChildIds[selectedChildIds.length - 1] ?? null;

    const scrollSelectedIntoCenter = useCallback(
        (animated = true) => {
            if (focusChildId == null || scrollViewWidthRef.current === 0) {
                return;
            }

            const layout = chipLayoutsRef.current.get(focusChildId);
            if (!layout) {
                return;
            }

            const chipCenter = layout.x + layout.width / 2;
            const maxScrollX = Math.max(
                0,
                contentWidthRef.current - scrollViewWidthRef.current,
            );
            const targetScrollX = Math.min(
                Math.max(0, chipCenter - scrollViewWidthRef.current / 2),
                maxScrollX,
            );

            scrollRef.current?.scrollTo({ x: targetScrollX, animated });
        },
        [focusChildId],
    );

    useEffect(() => {
        scrollSelectedIntoCenter(hasInitialScrollRef.current);
        hasInitialScrollRef.current = true;
    }, [scrollSelectedIntoCenter, childList]);

    const handleChipLayout = useCallback(
        (childId: number, x: number, width: number) => {
            chipLayoutsRef.current.set(childId, { x, width });

            if (childId === focusChildId) {
                scrollSelectedIntoCenter(hasInitialScrollRef.current);
            }
        },
        [scrollSelectedIntoCenter, focusChildId],
    );

    return (
        <ScrollView
            ref={scrollRef}
            horizontal
            showsHorizontalScrollIndicator={false}
            className="-mx-4"
            onLayout={(event) => {
                scrollViewWidthRef.current = event.nativeEvent.layout.width;
                scrollSelectedIntoCenter(false);
            }}
        >
            <View
                className="flex-row gap-2 px-4"
                onLayout={(event) => {
                    contentWidthRef.current = event.nativeEvent.layout.width;
                }}
            >
                {childList.map((child) => (
                    <ChildChip
                        key={child.id}
                        child={child}
                        isSelected={selectedChildIds.includes(child.id)}
                        onSelectChild={onSelectChild}
                        onLayout={handleChipLayout}
                    />
                ))}
            </View>
        </ScrollView>
    );
}

export function ChildSelector({
    childList,
    selectedChildIds,
    isLoading,
    onSelectChild,
    disabled = false,
}: ChildSelectorProps) {
    if (isLoading) {
        return (
            <Text variant="small" className="text-muted-foreground">
                Loading children…
            </Text>
        );
    }

    if (childList.length === 0) {
        return (
            <Text variant="small" className="text-muted-foreground">
                No linked children
            </Text>
        );
    }

    if (disabled) {
        const visibleChildren =
            selectedChildIds.length === 0
                ? childList
                : childList.filter((child) =>
                      selectedChildIds.includes(child.id),
                  );

        return (
            <View
                className={
                    Platform.OS === "web"
                        ? "flex-row flex-wrap gap-2"
                        : "flex-row"
                }
            >
                {visibleChildren.map((child) => (
                    <ChildChip
                        key={child.id}
                        child={child}
                        isSelected
                        disabled
                    />
                ))}
            </View>
        );
    }

    if (Platform.OS === "web") {
        return (
            <View className="flex-row flex-wrap gap-2">
                {childList.map((child) => (
                    <ChildChip
                        key={child.id}
                        child={child}
                        isSelected={selectedChildIds.includes(child.id)}
                        onSelectChild={onSelectChild}
                    />
                ))}
            </View>
        );
    }

    return (
        <NativeChildChipCarousel
            childList={childList}
            selectedChildIds={selectedChildIds}
            onSelectChild={onSelectChild}
        />
    );
}
